import {Component, Input, OnInit, OnChanges} from '@angular/core';
import {UserResponse, UserResultResponse} from '../../../../@shared/models/response.module';
import {EventsService} from '../../../../@shared/services/events-service';

interface EventResultGroup {
  event_id: number;
  event_name: string;
  event_date: string;
  results: UserResultResponse[];
}

@Component({
  selector: 'app-user-results',
  standalone: false,
  templateUrl: './user-results.html',
  styleUrl: './user-results.css'
})
export class UserResults implements OnInit, OnChanges {
  @Input() user: UserResponse;

  results: UserResultResponse[] = [];
  loading: boolean = true;
  selectedYear: number | null = null;
  availableYears: number[] = [];
  expandedEvents: Set<number> = new Set();

  // Stats
  totalResults: number = 0;
  wins: number = 0;
  podiums: number = 0;
  bestPosition: number | null = null;

  constructor(private eventsService: EventsService) {
  }

  ngOnInit() {
    if (this.user?.user_id) {
      this.loadUserResults();
    }
  }

  ngOnChanges() {
    if (this.user?.user_id) {
      this.loadUserResults();
    }
  }

  loadUserResults() {
    this.loading = true;
    this.eventsService.getUserResults(this.user.user_id).subscribe({
      next: (results) => {
        this.results = results || [];
        this.calculateStats();
        this.extractYears();
        this.loading = false;
      },
      error: () => {
        this.results = [];
        this.loading = false;
      }
    });
  }

  calculateStats() {
    this.totalResults = this.results.length;
    this.wins = this.results.filter(r => r.position === 1).length;
    this.podiums = this.results.filter(r => r.position >= 1 && r.position <= 3).length;

    const positions = this.results.filter(r => r.position > 0).map(r => r.position);
    this.bestPosition = positions.length > 0 ? Math.min(...positions) : null;
  }

  extractYears() {
    const years = new Set<number>();
    this.results.forEach(r => {
      if (r.event_date) {
        years.add(new Date(r.event_date).getFullYear());
      }
    });
    this.availableYears = Array.from(years).sort((a, b) => b - a);
  }

  get filteredResults(): UserResultResponse[] {
    if (!this.selectedYear) {
      return this.results;
    }
    return this.results.filter(r => {
      if (!r.event_date) return false;
      return new Date(r.event_date).getFullYear() === this.selectedYear;
    });
  }

  get groupedResults(): EventResultGroup[] {
    const grouped = new Map<number, EventResultGroup>();

    for (const result of this.filteredResults) {
      if (!grouped.has(result.event_id)) {
        grouped.set(result.event_id, {
          event_id: result.event_id,
          event_name: result.event_name,
          event_date: result.event_date,
          results: []
        });
      }
      grouped.get(result.event_id)!.results.push(result);
    }

    return Array.from(grouped.values()).sort((a, b) => {
      const dateA = a.event_date ? new Date(a.event_date).getTime() : 0;
      const dateB = b.event_date ? new Date(b.event_date).getTime() : 0;
      return dateB - dateA;
    });
  }

  toggleEventExpanded(eventId: number) {
    const newSet = new Set(this.expandedEvents);
    if (newSet.has(eventId)) {
      newSet.delete(eventId);
    } else {
      newSet.add(eventId);
    }
    this.expandedEvents = newSet;
  }

  isEventExpanded(eventId: number): boolean {
    return this.expandedEvents.has(eventId);
  }

  getPreviewResults(eventGroup: EventResultGroup): UserResultResponse[] {
    return eventGroup.results.slice(0, 3);
  }

  getHiddenResultsCount(eventGroup: EventResultGroup): number {
    return Math.max(0, eventGroup.results.length - 3);
  }

  getBestResult(eventGroup: EventResultGroup): UserResultResponse | null {
    const sorted = [...eventGroup.results].sort((a, b) => (a.position || 999) - (b.position || 999));
    return sorted[0] || null;
  }

  getPositionClass(position: number): string {
    if (position === 1) return 'gold';
    if (position === 2) return 'silver';
    if (position === 3) return 'bronze';
    return 'default';
  }

  getPositionIcon(position: number): string {
    if (position === 1) return 'fa-trophy';
    if (position === 2) return 'fa-medal';
    if (position === 3) return 'fa-award';
    return '';
  }

  formatResultTime(seconds: number | undefined | null): string {
    if (!seconds && seconds !== 0) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    const wholeSecs = Math.floor(secs);
    const ms = Math.round((secs - wholeSecs) * 1000);
    if (mins > 0) {
      return `${mins}:${wholeSecs.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
    }
    return `${wholeSecs}.${ms.toString().padStart(3, '0')}`;
  }

  getResultScore(result: UserResultResponse): string {
    if (result.final_time_seconds || result.raw_time_seconds) {
      return this.formatResultTime(result.final_time_seconds || result.raw_time_seconds);
    } else if (result.final_points !== null && result.final_points !== undefined) {
      return `${result.final_points} pts`;
    } else if (result.raw_points !== null && result.raw_points !== undefined) {
      return `${result.raw_points} pts`;
    } else if (result.status === 'dnf') {
      return 'DNF';
    } else if (result.status === 'dns') {
      return 'DNS';
    } else if (result.status === 'dq') {
      return 'DQ';
    }
    return '-';
  }

  formatDateDMYDots(dateValue: string | Date | null | undefined): string {
    if (!dateValue) return '';
    const date = new Date(dateValue);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  }
}
